import { Component, OnInit, Inject } from "@angular/core";
import { UntypedFormGroup, UntypedFormBuilder, Validators } from "@angular/forms";
import { GlobalService } from "../../services/global.service";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MasterService } from "../../services/master.service";
import { TranslateService } from "@ngx-translate/core";
import { takeUntil } from "rxjs/operators";
import { patternValidator } from "src/app/pattern-validator/pattern-validator";
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ConfirmDialogModalComponent } from "../confirm-dialog-modal/confirm-dialog-modal.component";
import { MatDialog } from "@angular/material/dialog";

@Component({
  selector: "app-manage-module-categories",
  templateUrl: "./manage-module-categories.component.html",
  styleUrls: ["./manage-module-categories.component.scss"]
})
export class ManageModuleCategoriesComponent implements OnInit {
  form: UntypedFormGroup;
  public moduleType: string;
  public moduleDisplayName: string;
  public permissionNameKey: string;
  public categories: any[] = [];
  public languages: any[] = [];
  public currentLang: string = 'en';
  public editMode: boolean = false;
  public editingCategory: any = null;
  public showForm: boolean = false;
  private draftCategoryNames: { [lang: string]: string } = {};

  constructor(
    private fb: UntypedFormBuilder,
    public globalService: GlobalService,
    private dialogRef: MatDialogRef<ManageModuleCategoriesComponent>,
    private masterService: MasterService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public translate: TranslateService,
    private dialog: MatDialog
  ) {
    this.moduleType = data.moduleType;
    this.moduleDisplayName = data.moduleDisplayName;
    const permissionKeyMap: Record<string, string> = {
      tour: 'tours',
      service: 'services',
      restaurant: 'restaurants'
    };
    this.permissionNameKey = permissionKeyMap[this.moduleType] || this.moduleType;
  }

  ngOnInit() {
    this.initForm();
    this.loadLanguages();
    this.loadCategories();
  }

  ngOnDestroy() {
    // Cleanup if needed
  }

  initForm() {
    this.form = this.fb.group({
      CategoryName: ["", [Validators.required, patternValidator(/^[^<>%$]*$/)]],
      CategoryColor: ['#008BEC']  
    });
  }

  loadLanguages() {
    // Get available languages from hotel setup
    const hotelLangs = JSON.parse(window.localStorage.getItem("SelectedLanguages") || "[]");
    this.languages = hotelLangs.map((lang: any) => ({
      code: lang.LangKey,
      name: lang.LangName
    }));
    
    if (this.languages.length === 0) {
      this.languages = [{ code: 'en', name: 'English' }];
    }

    const englishExists = this.languages.some((lang: any) => lang.code === 'en');
    this.currentLang = englishExists ? 'en' : this.languages[0].code;
  }

  private resetToPreferredLanguage() {
    this.loadLanguages();
    if (this.showForm) {
      const draftValue = this.draftCategoryNames[this.currentLang] || '';
      this.form.patchValue({
        CategoryName: draftValue,
      });
    }
    this.loadCategories();
  }

  private getListLanguage(): string {
    const uiLang = (this.globalService.gethotelLangTranslate() || "").toString().trim().toLowerCase();
    if (uiLang) {
      return uiLang.split("-")[0];
    }
    return "en";
  }

  loadCategories() {
    const listLang = this.getListLanguage();
    this.globalService.showLoader = true;
    this.masterService
      .fetchModuleCategories({
        ModuleType: this.moduleType,
        LangKey: listLang,
        IncludeAllLangs: true,
        Route: this.permissionNameKey
      })
      .pipe(takeUntil(this.globalService.componentDestroyed(this)))
      .subscribe(
        response => {
          this.globalService.showLoader = false;
          if (response.Status) {
            this.categories = response.Data.categories || [];
          } else {
            this.categories = [];
          }
        },
        error => {
          this.globalService.showLoader = false;
          this.globalService.handleApiError(error);
        }
      );
  }

  switchLanguage(lang: string) {
    // Preserve current input before switching language
    const currentValue = this.form?.value?.CategoryName;
    if (this.showForm && typeof currentValue === 'string') {
      this.draftCategoryNames[this.currentLang] = currentValue;
    }

    this.currentLang = lang;
    // Update form with current language value if editing
    if (this.editingCategory) {
      const langValue = this.getCategoryNameForLang(this.editingCategory, lang);
      this.form.patchValue({
        CategoryName: langValue
      });
    } else if (this.showForm) {
      const draftValue = this.draftCategoryNames[lang] || '';
      this.form.patchValue({
        CategoryName: draftValue
      });
    }

    // Refresh list only when form is closed
    if (!this.showForm) {
      this.loadCategories();
    }
  }

  getCategoryName(category: any): string {
    if (!category || !category.CategoryNames || category.CategoryNames.length === 0) {
      return '';
    }
    
    // List language is always based on selected UI language, not edit popup language
    const listLang = this.getListLanguage();
    const currentLangName = category.CategoryNames.find((n: any) =>
      ((n.Lang || "").toString().trim().toLowerCase().split("-")[0]) === listLang
    );
    if (currentLangName && currentLangName.Value) {
      return currentLangName.Value;
    }
    
    // Fallback to first available
    return category.CategoryNames[0].Value || '';
  }

  getCategoryNameForLang(category: any, lang: string): string {
    if (!category || !category.CategoryNames || category.CategoryNames.length === 0) {
      return '';
    }
    const langName = category.CategoryNames.find((n: any) => n.Lang === lang);
    return langName ? langName.Value : '';
  }

  addNew() {
    this.editMode = false;
    this.editingCategory = null;
    this.showForm = true;
    // this.form.reset();
    this.form.reset({
    CategoryName: '',
    CategoryColor: '#008BEC'
  });
    this.draftCategoryNames = {};
    this.resetToPreferredLanguage();
  }

  editCategory(category: any) {
    this.editMode = true;
    this.editingCategory = category;
    this.showForm = true;

    // Seed drafts from existing names so switching languages keeps values
    this.draftCategoryNames = {};
    if (category && category.CategoryNames) {
      category.CategoryNames.forEach((entry: any) => {
        if (entry && entry.Lang) {
          this.draftCategoryNames[entry.Lang] = entry.Value || '';
        }
      });
    }
    
    const categoryName = this.getCategoryNameForLang(category, this.currentLang);
    this.form.patchValue({
      CategoryName: categoryName,
      CategoryColor: category.CategoryColor || '#008BEC'
    });
  }

  cancelForm() {
    this.showForm = false;
    this.editMode = false;
    this.editingCategory = null;
    // this.form.reset();
     this.form.reset({
    CategoryName: '',
    CategoryColor: '#008BEC'
  });
    this.draftCategoryNames = {};
    this.resetToPreferredLanguage();
  }

  saveCategory() {
    if (!this.form.valid) {
      return;
    }

    const categoryName = this.form.value.CategoryName;

    // Capture current language value into drafts
    this.draftCategoryNames[this.currentLang] = categoryName;
    
    // Build CategoryNames array for all languages
    let categoryNames: any[] = [];
    
    if (this.editMode && this.editingCategory) {
      // Start with existing names
      categoryNames = [...this.editingCategory.CategoryNames];

      // Merge drafts into existing names
      Object.keys(this.draftCategoryNames).forEach((lang) => {
        const value = this.draftCategoryNames[lang];
        const existingIndex = categoryNames.findIndex(n => n.Lang === lang);
        if (existingIndex >= 0) {
          categoryNames[existingIndex].Value = value;
        } else if (value !== '') {
          categoryNames.push({
            Lang: lang,
            Value: value
          });
        }
      });
      
    } else {
      // New category - use drafts for all languages
      categoryNames = Object.keys(this.draftCategoryNames).map((lang) => ({
        Lang: lang,
        Value: this.draftCategoryNames[lang]
      }));

      if (!categoryNames.length) {
        categoryNames = [{
          Lang: this.currentLang,
          Value: categoryName
        }];
      }
    }

    if (this.editMode) {
      // Update existing category
      this.globalService.showLoader = true;
      this.masterService
        .updateModuleCategory({
          Lang: this.masterService.getLangSaveProperty(),
          CategoryId: this.editingCategory._id,
          CategoryNames: categoryNames,
          Order: this.editingCategory.Order,
          IsActive: this.editingCategory.IsActive !== undefined ? this.editingCategory.IsActive : true,
          CategoryColor: this.form.value.CategoryColor,
          ModuleType: this.moduleType,
          Route: this.permissionNameKey
        })
        .pipe(takeUntil(this.globalService.componentDestroyed(this)))
        .subscribe(
          response => {
            this.globalService.showLoader = false;
            if (response.Status) {
              this.globalService.handleSuccessMessage(
                this.translate.instant('CategoryUpdatedSuccess')
              );
              this.loadCategories();
              this.cancelForm();
            } else {
              this.globalService.handleApiError(response);
            }
          },
          error => {
            this.globalService.showLoader = false;
            this.globalService.handleApiError(error);
          }
        );
    } else {
      // Add new category
      this.globalService.showLoader = true;
      this.masterService
        .addModuleCategory({
          Lang: this.masterService.getLangSaveProperty(),
          ModuleType: this.moduleType,
          CategoryNames: categoryNames,
          Order: this.categories.length,
          CategoryColor: this.form.value.CategoryColor,
          Route: this.permissionNameKey
        })
        .pipe(takeUntil(this.globalService.componentDestroyed(this)))
        .subscribe(
          response => {
            this.globalService.showLoader = false;
            if (response.Status) {
              this.globalService.handleSuccessMessage(
                this.translate.instant('CategoryAddedSuccess')
              );
              this.loadCategories();
              this.cancelForm();
            } else {
              this.globalService.handleApiError(response);
            }
          },
          error => {
            this.globalService.showLoader = false;
            this.globalService.handleApiError(error);
          }
        );
    }
  }

  deleteCategory(category: any) {
    const dialogRef = this.dialog.open(ConfirmDialogModalComponent, {
      width: "400px",
      data: {
        title: this.translate.instant('DeleteCategory'),
        message: this.translate.instant('DeleteCategoryConfirm')
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.globalService.showLoader = true;
        this.masterService
          .deleteModuleCategory(category._id, {
            Lang: this.masterService.getLangSaveProperty(),
            ModuleType: this.moduleType,
            Route: this.permissionNameKey
          })
          .pipe(takeUntil(this.globalService.componentDestroyed(this)))
          .subscribe(
            response => {
              this.globalService.showLoader = false;
              if (response.Status) {
                this.globalService.handleSuccessMessage(
                  this.translate.instant('CategoryDeletedSuccess')
                );
                this.loadCategories();
              } else {
                this.globalService.handleApiError(response);
              }
            },
            error => {
              this.globalService.showLoader = false;
              this.globalService.handleApiError(error);
            }
          );
      }
    });
  }

  drop(event: CdkDragDrop<any[]>) {
    moveItemInArray(this.categories, event.previousIndex, event.currentIndex);
    
    // Update order for all categories
    const categoriesWithOrder = this.categories.map((cat, index) => ({
      id: cat._id,
      Order: index
    }));

    this.globalService.showLoader = true;
    this.masterService
      .reorderModuleCategories({
        Lang: this.masterService.getLangSaveProperty(),
        Categories: categoriesWithOrder,
        ModuleType: this.moduleType,
        Route: this.permissionNameKey
      })
      .pipe(takeUntil(this.globalService.componentDestroyed(this)))
      .subscribe(
        response => {
          this.globalService.showLoader = false;
          if (!response.Status) {
            this.globalService.handleApiError(response);
            // Reload to get correct order
            this.loadCategories();
          }
        },
        error => {
          this.globalService.showLoader = false;
          this.globalService.handleApiError(error);
          this.loadCategories();
        }
      );
  }

  close() {
    this.showForm = false;
    this.editMode = false;
    this.editingCategory = null;
    this.form.reset();
    this.draftCategoryNames = {};
    this.resetToPreferredLanguage();
    this.dialogRef.close(true);
  }
}
